import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCurrentUser, useHasRole } from '@client/shared/auth';
import { fetchViewer, VIEWER_QUERY_KEY, type Viewer } from './viewer-operations.ts';
import { dismissMyTutorial } from './tutorial-operations.ts';

export type TutorialFlow = 'agent' | 'admin';

/**
 * First-run tutorial state, read off the shared viewer query. flow is null once
 * dismissed (or while loading), so screens can gate their coach-marks on it
 * directly. dismiss() hides the marks immediately and persists server-side; if
 * that write fails, the tutorial simply re-appears on a later load.
 */
export function useTutorial(): { flow: TutorialFlow | null; dismiss: () => void } {
  const isAdmin = useHasRole('admin', 'owner');
  const signedIn = useCurrentUser() !== null;
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: VIEWER_QUERY_KEY,
    queryFn: ({ signal }) => fetchViewer(signal),
    enabled: signedIn,
    staleTime: 30_000,
  });

  const { mutate } = useMutation({ mutationFn: dismissMyTutorial });

  const dismiss = useCallback(() => {
    queryClient.setQueryData<Viewer>(VIEWER_QUERY_KEY, (viewer) =>
      viewer ? { ...viewer, tutorialDismissedAt: new Date().toISOString() } : viewer,
    );
    mutate();
  }, [queryClient, mutate]);

  const active = query.data?.tutorialDismissedAt === null;
  const flow = active ? (isAdmin ? 'admin' : 'agent') : null;
  return { flow, dismiss };
}
